import styled from "@emotion/styled"
import { useEffect, useMemo, useRef, useState } from "react"

import { MsEyebrow } from "../ms"
import { paper, fonts, shade } from "../theme"
import type { CommonRoomData, PhaseView, PlayerInfo } from "../protocol"
import { TvBand, TvRoster, TvStage } from "./TvStage"
import { TvDeck, TvDisplay } from "./tvType"
import type { BestEntry } from "./screens"
import {
    TvBuild, TvDraw, TvGameOver, TvHeadline, TvIntermission, TvLobby, TvReveal,
    TvRoundIntro, TvSourcePick, TvStandings, TvVote, TvWinner,
} from "./screens"
import { useTvRoom } from "./useTvRoom"
import { demoParams, useTvDemo } from "./tvDemo"
import { useCastReceiver } from "./useCastReceiver"
import { usePresentationReceiverRoom } from "./usePresentationReceiver"
import { isDisplayContext } from "../cast/castConfig"

/**
 * The TV view.
 *
 * A display is a projection of the same snapshots the phones get, so this is the
 * same shape as App.tsx: read the phase, render a screen. The differences are
 * all consequences of having no seat — nothing here sends a message, there is no
 * `self` to render from, and it never navigates.
 *
 * Two things the server doesn't send and the TV accumulates for itself: the
 * night's best misquote, and the articles a round drew on. Both arrive earlier in
 * the round than the screen that wants them, so remembering beats widening the
 * protocol. A screen switched on mid-game simply has less to show, which is the
 * correct degradation.
 */

/** How long the round number holds the screen before the deck appears. */
const ROUND_INTRO_MS = 3200

interface Accumulated {
    best: BestEntry | null
    articles: string[]
}

export default function TvApp({ initialCode }: { initialCode: string | null }) {
    const [entered, setEntered] = useState(initialCode)
    const cast = useCastReceiver()
    const presented = usePresentationReceiverRoom()

    /*
     * Three ways a display learns its room, in order of how recently it was
     * told. A controller can re-point a screen mid-session, so a room arriving
     * over either channel outranks the one this page loaded with.
     *
     *  - the Presentation API channel (the standard transport),
     *  - the Cast custom-message channel,
     *  - the URL, which is how a presentation is launched and how a person
     *    typing a code with a remote gets here.
     */
    const code = presented ?? (cast.kind === "room" ? cast.code : entered)
    const live = useTvRoom(demoParams() ? null : code)
    const demo = useTvDemo()
    const room = demo ?? live

    const accumulated = useAccumulated(room.phase, room.room, room.view)
    const showRoundIntro = useRoundIntro(room.phase, room.room?.round ?? 0)

    if (!code && !demo) {
        // Only ask for a code where a person could plausibly type one. A screen
        // put here by a controller waits to be told instead.
        return !isDisplayContext()
            ? <TvCodeEntry onSubmit={setEntered} />
            : (
                <TvStage>
                    <TvIntermission
                        message="Ready to cast."
                        detail="Start a game on your phone and pick this screen."
                    />
                </TvStage>
            )
    }

    // Connection states get the whole canvas: a screen that can't show the game
    // should say so in type you can read from the sofa, not hide it in a corner.
    if (room.status.kind === "refused") {
        return (
            <TvStage>
                <TvIntermission stamp="No room" message={room.status.reason} detail={code ?? undefined} />
            </TvStage>
        )
    }
    if (!room.room || !room.view || !room.phase) {
        return (
            <TvStage>
                <TvIntermission
                    message={room.status.kind === "reconnecting" ? "Reconnecting." : "Connecting."}
                    detail={code ?? undefined}
                />
            </TvStage>
        )
    }

    const { room: common, view } = room
    const players = common.players
    const dealerId = pickerIdFor(view, players)
    const roundArticles = accumulated.articles

    // Narrowing on `view.type` rather than `phase`: the views are a discriminated
    // union, so each branch hands its screen a properly typed view instead of a
    // cast that would quietly survive the protocol changing underneath it.
    const screen = (() => {
        switch (view.type) {
            case "waiting_room":
                return <TvLobby code={common.roomCode} players={players} />

            case "prompt_entry":
            case "digital_prompt_entry": {
                const dealer = view.picker?.name ?? null
                if (view.promptText) {
                    return <TvHeadline prompt={view.promptText} dealer={dealer} />
                }
                if (showRoundIntro) {
                    return <TvRoundIntro round={common.round} totalRounds={common.totalRounds} dealer={dealer} />
                }
                return <TvDraw dealer={dealer} />
            }

            case "select_article":
                return <TvSourcePick view={view} players={players} />

            case "word_picker":
                return <TvBuild view={view} players={players} />

            case "results":
                return <TvReveal view={view} />

            case "voting":
                return <TvVote view={view} players={players} />

            case "voting_results":
                return <TvWinner view={view} players={players} />

            case "round_leaderboard":
                return (
                    <TvStandings view={view} players={players} articles={roundArticles} />
                )

            case "game_over":
                return <TvGameOver view={view} players={players} best={accumulated.best} />

            default:
                return <TvIntermission message="Stand by." />
        }
    })()

    // The lobby carries the code in its own hero type, and game over is a full
    // stop — neither wants the running chrome over the top of it.
    const inLobby = view.type === "waiting_room"
    const showChrome = !inLobby && view.type !== "game_over"
    const scores = scoresFrom(view)

    return (
        <TvStage
            band={showChrome ? (
                <TvBand
                    round={common.round}
                    totalRounds={common.totalRounds}
                    code={common.roomCode}
                    timeoutTime={common.timeoutTime}
                    phaseStartTime={common.phaseStartTime}
                />
            ) : undefined}
            roster={showChrome ? (
                <TvRoster
                    players={players}
                    dealerId={dealerId}
                    scores={scores?.scores}
                    gains={scores?.gains}
                />
            ) : undefined}
        >
            {screen}
        </TvStage>
    )
}

/* ── Derivations ────────────────────────────────────────────────────────── */

/** Who's holding the deck, when the phase says. */
function pickerIdFor(view: PhaseView, players: PlayerInfo[]): string | null {
    if (view && typeof view === "object" && "picker" in view) {
        const picker = (view as { picker?: PlayerInfo | null }).picker
        if (picker) return picker.id
    }
    if (view && typeof view === "object" && "presenterName" in view) {
        const name = (view as { presenterName?: string }).presenterName
        return players.find(p => p.name === name)?.id ?? null
    }
    return null
}

/** Running totals for the roster strip, where the phase happens to know them. */
function scoresFrom(view: PhaseView): { scores: Record<string, number>; gains: Record<string, number> } | null {
    if (!view || typeof view !== "object") return null
    if ("scores" in view) {
        const v = view as { scores: Record<string, number>; roundResults?: Record<string, number> }
        return { scores: v.scores, gains: v.roundResults ?? {} }
    }
    if ("finalScores" in view) {
        return { scores: (view as { finalScores: Record<string, number> }).finalScores, gains: {} }
    }
    return null
}

/**
 * Remember the two things the standings and the final edition ask for but
 * aren't sent with them: the round's articles (which arrive with the reveal)
 * and the night's best-voted entry (which arrives with each round's result).
 */
function useAccumulated(phase: string | null, room: CommonRoomData | null, view: PhaseView | null): Accumulated {
    const [accumulated, setAccumulated] = useState<Accumulated>({ best: null, articles: [] })

    useEffect(() => {
        if (!phase || !view || typeof view !== "object") return

        if (phase === "RESULTS" && "entries" in view) {
            const entries = (view as { entries: { article?: { title: string } | null }[] }).entries
            const titles = [...new Set(entries.map(e => e.article?.title).filter((t): t is string => !!t))]
            setAccumulated(prev =>
                titles.join("|") === prev.articles.join("|") ? prev : { ...prev, articles: titles })
        }

        if (phase === "VOTING_RESULTS" && "results" in view && room) {
            const v = view as { results: Record<string, number>; answers: Record<string, string | null> }
            const [id, votes] = Object.entries(v.results).sort((a, b) => b[1] - a[1])[0] ?? []
            if (!id || !votes) return
            setAccumulated(prev =>
                votes > (prev.best?.votes ?? 0)
                    ? {
                        ...prev,
                        best: {
                            answer: v.answers[id] ?? "",
                            author: room.players.find(p => p.id === id)?.name ?? "—",
                            votes,
                        },
                    }
                    : prev)
        }
    }, [phase, view, room])

    return accumulated
}

/**
 * True for the first few seconds of a new round, while the numeral holds.
 *
 * No "have I already shown this round?" ref. One used to guard the body, which
 * made the effect non-idempotent: StrictMode's mount / unmount / remount ran it,
 * cancelled the timer in cleanup, then hit the guard and never scheduled another
 * — leaving the round card on screen for the rest of the round. Keying on the
 * round and letting cleanup cancel says the same thing and survives being run
 * twice, which is the only kind of effect worth writing.
 */
function useRoundIntro(phase: string | null, round: number): boolean {
    const [showing, setShowing] = useState(false)

    useEffect(() => {
        const isPromptPhase = phase === "PROMPT_ENTRY" || phase === "DIGITAL_PROMPT_ENTRY"
        if (!isPromptPhase || round <= 0) {
            setShowing(false)
            return
        }

        setShowing(true)
        const id = setTimeout(() => setShowing(false), ROUND_INTRO_MS)
        return () => clearTimeout(id)
    }, [phase, round])

    return showing
}

/* ── Code entry ─────────────────────────────────────────────────────────── */

const EntryForm = styled.form`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 28px;
`

const CodeInput = styled.input`
    font-family: ${fonts.serif};
    font-weight: 800;
    font-size: 160px;
    letter-spacing: 0.12em;
    text-align: center;
    text-transform: uppercase;
    width: 640px;
    padding: 20px 0;
    background: ${paper.paperCool};
    color: ${paper.ink};
    border: none;
    border-bottom: 6px solid ${paper.red};
    border-radius: 8px 8px 0 0;
    outline: none;
    box-shadow: 0 0 0 1px ${shade(0.1)}, 0 20px 40px -24px ${shade(0.5)};

    &::placeholder { color: ${shade(0.18)}; }
`

/**
 * Bare /tv: a screen that hasn't been told which room to show.
 *
 * Reachable two ways — a smart TV browser opened by hand, and a Cast receiver
 * launched before its sender has sent a room. Typing four characters is about
 * the most a TV remote should ever be asked to do, and the input is autofocused
 * so a paired keyboard or phone remote can just start typing.
 */
function TvCodeEntry({ onSubmit }: { onSubmit: (code: string) => void }) {
    const [value, setValue] = useState("")
    const ref = useRef<HTMLInputElement>(null)

    useEffect(() => { ref.current?.focus() }, [])

    const clean = useMemo(() => value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4), [value])

    return (
        <TvStage>
            <div style={{
                flex: 1, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", gap: 20, textAlign: "center",
            }}>
                <TvDisplay size={104}>Enter the room code</TvDisplay>
                <TvDeck size={28}>It's on the host's phone.</TvDeck>
                <EntryForm
                    onSubmit={e => {
                        e.preventDefault()
                        if (clean.length >= 4) onSubmit(clean)
                    }}
                >
                    <CodeInput
                        ref={ref}
                        value={clean}
                        onChange={e => setValue(e.target.value)}
                        placeholder="––––"
                        inputMode="text"
                        autoComplete="off"
                        spellCheck={false}
                        aria-label="Room code"
                    />
                    <MsEyebrow tone="mute">Press enter when it's in</MsEyebrow>
                </EntryForm>
            </div>
        </TvStage>
    )
}
